import { useEffect, useMemo } from 'react';
import { Card, Box, Typography, Button, CardMedia, FormControlLabel, Switch, Chip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import moment from 'moment';
import 'moment/locale/es';

import { urlImg, ColorButton } from '../../common/helpers';

import { useSelector, useDispatch } from 'react-redux';
import { updateProfilePageStatus } from '../../../actions/profile';

const PageCard = ({ page, type, onEdit, onDelete, onView }) => {
  const categories = useSelector(state => state.cat.categories);
  const isDraft = type === 'DRAFT';
  const dateLabel = isDraft ? 'Creado el' : 'Publicado el';
  const dateValue = moment(page.date).format('LL');

  const categoriaName = useMemo(() => {
    if (!page?.categoria) return null;
    // nombre resuelto por el backend (funciona aunque la categoría esté deshabilitada)
    if (page.categoriaNombre) return page.categoriaNombre;
    // si la propiedad ya viene como objeto con name
    if (typeof page.categoria === 'object') return page.categoria.name || null;
    // si viene como id (string) buscar en el store de categorías activas
    const found = categories?.find(cat => cat._id === page.categoria);
    // nunca mostrar el _id crudo: si no se resuelve, mostrar etiqueta genérica
    return found ? found.name : 'Sin categoría';
  }, [page?.categoria, page?.categoriaNombre, categories]);

  const misServicios = useSelector(state => state.profile.profile.servicios);

  // Servicios de la página que no son de su categoría (páginas guardadas antes de que la categoría fuera obligatoria
  // para sus servicios). Se avisa aquí para que el dueño sepa cuáles corregir.
  const fueraDeCategoria = useMemo(() => {
    if (!page?.categoria) return 0;
    const ids = new Set((page.servicios || []).map(ps => String(ps?.servicio?._id || ps?.servicio || ps)));
    return (misServicios || []).filter(s => ids.has(String(s._id)) && String(s.categoria) !== String(page.categoria)).length;
  }, [page?.categoria, page?.servicios, misServicios]);

  // Grupo/negocio o independiente: el tipo guardado (las páginas de antes: grupo si tienen a alguien más en el equipo)
  const esGrupo = page.tipo ? page.tipo === 'grupo' : (page.miembros || []).length > 1;
  // Servicios cuya persona elegida para impartirlos aún no acepta (o rechazó): el público no los ve ni puede contratarlos
  const enEspera = (page.servicios || []).filter(ps => ps?.estadoFacilitador === 'pendiente').length;
  const rechazados = (page.servicios || []).filter(ps => ps?.estadoFacilitador === 'rechazado').length;
  // Cuántos servicios muestra de verdad la página (lo calcula el servidor); antes se contaban todos los vinculados
  const visibles = typeof page.serviciosVisibles === 'number' ? page.serviciosVisibles : (page.servicios ? page.servicios.length : 0);
  const oculta = !isDraft && page.status && typeof page.serviciosVisibles === 'number' && page.serviciosVisibles === 0;

  const dispatch = useDispatch();

  const cambiarStatus = async status => {
    await dispatch(updateProfilePageStatus({ status }, page._id));
  };

  return (
    <Card className="card-edit" sx={{ height: 'auto', minHeight: { md: 200 }, mb: 2.5, flexDirection: { xs: 'column', sm: 'row' }, overflow: 'hidden' }}>
      <CardMedia
        component="img"
        className='picture-item'
        sx={{ width: { xs: '100%', sm: 140 }, height: { xs: 160, sm: 'auto' }, minHeight: 160, flexShrink: 0, alignSelf: 'stretch', objectFit: 'cover' }}
        image={page.img && page.img.length > 0 ? `${urlImg}/paginas/${page._id}/${page.img}` : '/search.png'}
        alt={page.nombre}
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, m: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
              <Typography variant="h6" fontWeight="bold" color="primary">
                {page.nombre || 'Sin título'}
              </Typography>
              <Box
                sx={{
                  px: 1,
                  py: 0.2,
                  borderRadius: '4px',
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                  bgcolor: esGrupo ? ((t) => alpha(t.palette.success.main, 0.12)) : ((t) => alpha(t.palette.info.main, 0.12)),
                  color: esGrupo ? 'success.oscuro' : 'primary.main',
                  fontSize: '0.7rem',
                  fontWeight: 'bold',
                  textTransform: 'uppercase'
                }}
              >
                {esGrupo ? 'Grupo / Negocio' : 'Independiente'}
              </Box>
            </Box>
            <Typography variant="body2" sx={{
              mt: 1, mb: 2,
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}>
              {page.descripcion || 'Sin descripción'}
            </Typography>

            <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', mb: 2 }}>
              {/* Duration - Mocked for now as it wasn't in the form explicitly, or maybe derived */}
              {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <AccessTimeIcon fontSize="small" color="action" />
                <Typography variant="caption" color="text.secondary">Duración: 3 horas</Typography>
              </Box> */}

              {/* {page.ubicacion && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LocationOnIcon fontSize="small" color="action" />
                  <Typography variant="caption">{page.ubicacion}</Typography>
                </Box>
              )} */}

              {page.categoria && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LocalOfferIcon fontSize="small" color="action" />
                  <Typography variant="caption">{categoriaName}</Typography>
                </Box>
              )}
            </Box>

            {fueraDeCategoria > 0 && (
              <Chip
                size="small"
                icon={<WarningAmberIcon sx={{ color: 'primary.main !important' }} />}
                label={`${fueraDeCategoria} ${fueraDeCategoria === 1 ? 'servicio no es' : 'servicios no son'} de la categoría de la página`}
                sx={{ mb: 2, bgcolor: 'warning.main', color: 'primary.main', fontWeight: 600, maxWidth: '100%' }}
              />
            )}
            {/* Lo que el dueño necesita saber de quién imparte sus servicios; al tocarlo va a editar la página */}
            {(enEspera > 0 || rechazados > 0 || oculta) && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {enEspera > 0 && (
                  <Chip
                    size="small"
                    clickable
                    onClick={onEdit}
                    label={`${enEspera} ${enEspera === 1 ? 'servicio espera' : 'servicios esperan'} a que acepten impartirlo${enEspera === 1 ? '' : 's'}`}
                    sx={{ bgcolor: 'warning.main', color: 'primary.main', fontWeight: 600, maxWidth: '100%' }}
                  />
                )}
                {rechazados > 0 && (
                  <Chip
                    size="small"
                    clickable
                    onClick={onEdit}
                    label={`${rechazados} ${rechazados === 1 ? 'invitación rechazada' : 'invitaciones rechazadas'}: elige a otra persona`}
                    sx={{ bgcolor: 'error.main', color: 'primary.main', fontWeight: 600, maxWidth: '100%' }}
                  />
                )}
                {oculta && (
                  <Chip
                    size="small"
                    clickable
                    onClick={onEdit}
                    icon={<WarningAmberIcon sx={{ color: 'primary.main !important' }} />}
                    label="El público aún no la ve: ningún servicio disponible"
                    sx={{ bgcolor: 'warning.main', color: 'primary.main', fontWeight: 600, maxWidth: '100%' }}
                  />
                )}
              </Box>
            )}
          </Box>

          {!isDraft && (
            <Box sx={{ textAlign: 'center', m: 2 }}>
              <Box sx={{
                width: 60, height: 60, display: 'flex', flexDirection: 'column',
                justifyContent: 'center', alignItems: 'center'
              }}>
                <Typography variant="h4" fontWeight="bold" lineHeight={1}>
                  {visibles}
                </Typography>
                <Typography variant='body2'>
                  Servicios disponibles
                </Typography>
              </Box>
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 1, mt: 'auto' }}>
          <Typography variant="caption">
            {dateLabel} {dateValue}
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', width: { xs: '100%', sm: 'auto' } }}>
            {!isDraft && (
              <Box className="toggle" sx={{ display: 'flex', alignItems: 'center', width: { xs: '100%', sm: 'auto' }, mr: { sm: 1 } }}>
                <Typography variant="body2" color="text.secondary" sx={{ marginRight: 1 }}>
                  {page?.status ? "Activo" : "Inactivo"}
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={page?.status || false}
                      onChange={(e) => cambiarStatus(e.target.checked)}
                      name="status"
                      size="small"
                    />
                  }
                  label=""
                  sx={{ m: 0 }}
                />
              </Box>
            )}
            <Button
              variant="contained"
              color="success"
              size="small"
              onClick={onView}
              sx={{ color: "white", minWidth: 0, flex: { xs: 1, sm: 'none' }, mr: { sm: "0.5rem" }, fontSize: "14px", textTransform: "none" }}
              endIcon={<VisibilityIcon fontSize="small" />}
            >
              Ver
            </Button>
            <ColorButton
              variant="contained"
              color="primary"
              size="small"
              onClick={onEdit}
              sx={{ color: "white", minWidth: 0, flex: { xs: 1, sm: 'none' }, mr: { sm: "0.5rem" }, fontSize: "14px", textTransform: "none" }}
              endIcon={<EditIcon fontSize="small" />}
            >
              Editar
            </ColorButton>
            <Button
              variant="contained"
              color="error"
              size="small"
              onClick={onDelete}
              sx={{ color: "white", minWidth: 0, flex: { xs: 1, sm: 'none' }, mr: { sm: "0.5rem" }, fontSize: "14px", textTransform: "none" }}
              endIcon={<DeleteIcon fontSize="small" />}
            >
              Eliminar
            </Button>
          </Box>
        </Box>
      </Box>
    </Card>
  );
};

export default PageCard;
